import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { Link, useHistory } from 'react-router-dom'
import messages from './messages'
import { FormattedMessage, useIntl } from 'react-intl'
import { isValidEmail } from 'utils'
import BoxButton from 'components/BoxButton'
import { Button } from '@arnsbogroup/react-voicearchive'

const Form = ({ match: { url, params: { supplierId } }}) => {
  const { formatMessage } = useIntl()
  const history = useHistory()

  const [loading, setLoading] = useState(false)
  const [state, setState] = useState({
    email: '',
    password: '',
    referer: null,
    referer_note: '',
    application: '',
  })

  const [errors, setErrors] = useState(
    Object.keys(state).reduce((memo, key) => {
      memo[key] = true
      return memo
    }, {})
  )

  const referers = useMemo(() => ({
    SEARCH : {
      key: 'SEARCH',
      icon: ['fal', 'search'],
      name: formatMessage(messages.refererSearch)
    },
    SOME: {
      key: 'SOME',
      icon: ['fal', 'thumbs-up'],
      name: formatMessage(messages.refererSoMe)
    },
    OFFLINE: {
      key: 'OFFLINE',
      icon: ['fal', 'users-class'],
      name: formatMessage(messages.refererOffline)
    },
    FRIEND: {
      key: 'FRIEND',
      icon: ['fal', 'user-friends'],
      name: formatMessage(messages.refererFriend)
    },
    OTHER: {
      key: 'OTHER',
      icon: ['fal', 'share-alt'],
      name: formatMessage(messages.refererOther)
    },
  }), [])

  const validate = useCallback(() => {
    setErrors({
      email: !!state.email && isValidEmail(state.email),
      application: !!state.application && state.application.length > 20,
      password: !!state.password && state.password.length > 11,
    })
  }, [state])

  const onSubmit = (event) => {
    setLoading(true)

    if (Object.values(errors).some((valid) => !valid)) {
      event.stopPropagation()
      event.preventDefault()

      setLoading(false)

      return window.alert(formatMessage(messages.validationFailed))
    }

    fetch(`/rest/v2/supplier`, {
      method: 'POST',
      body: JSON.stringify(state)
    })
      .then((response) => response.json())
      .then(({ success, data, errors }) => {
        if (success) {
          return history.push({
            pathname: `${url}/submitted`,
            state: { success: true }
          })
        }
        
        setLoading(false)
        window.alert(errors.join(' - '))
      })
      .catch((error) => {
        setLoading(false)
        window.alert(error)
      })
  }

  return (
    <div className="w-full h-full md:flex">
      <div className="py-10 md:h-auto w-full md:w-1/3 bg-primary flex justify-center items-center">
        <div className="w-2/3">
          <FormattedMessage {...messages.logoSrc}>
            { (src) => (
              <img src={src} className="h-12 mb-12" />
            ) }
          </FormattedMessage>

          <div className="text-white font-medium text-4xl">
            <FormattedMessage {...messages.title } />
          </div>

          <div className="text-white mt-6 w-2/3">
            <FormattedMessage {...messages.subtitle } />
          </div>
        </div>
      </div>
      <div className="md:h-full w-full md:w-2/3 md:overflow-scroll p-10">
        <div className="w-2/3 m-auto py-12">
          <section>
            <p className="py-4">
              <FormattedMessage {...messages.emailLabel } /> *
            </p>

            <input
              type="email"
              value={state.email}
              onChange={({ target: { value } }) => setState({ ...state, email: value })}
              placeholder={formatMessage(messages.emailPlaceholder)}
              className={`w-1/2 p-4 focus:border-primary transition duration-200 border ${errors.email ? 'border-gray-400' : 'border-red-600'}`}
              onBlur={validate}
            />
          </section>

          <section className="mt-6">
            <p className="py-4">
              <FormattedMessage {...messages.passwordLabel } /> *
              <div className="text-sm text-gray-700" >
                <FormattedMessage {...messages.passwordLabelHint } />
              </div>
            </p>

            <input
              type="password"
              value={state.password}
              onChange={({ target: { value } }) => setState({ ...state, password: value })}
              placeholder={formatMessage(messages.passwordPlaceholder)}
              className={`w-1/2 p-4 focus:border-primary transition duration-200 border ${errors.password ? 'border-gray-400' : 'border-red-600'}`}
              onBlur={validate}
            />
          </section>

          <section className="mt-6">
            <p className="py-4">
              <FormattedMessage {...messages.refererLabel } /> *
              <div className="text-sm text-gray-700" >
                <FormattedMessage {...messages.refererLabelHint } />
              </div>
            </p>

            <div className="flex justify-start flex-wrap">
              { Object.values(referers).map(({ key, icon, selected, name }) => (
                <div className="inline-block mr-4 mt-4" key={key}>
                  <BoxButton
                    key={key}
                    icon={icon}
                    selected={state.referer == key}
                    onClick={() => setState((state) => ({ ...state, referer: key }))}
                  >
                    { name }
                  </BoxButton>
                </div>
              )) }

              { state.referer == 'OTHER' &&
                <textarea
                  className="inline-block mr-4 mt-4 w-40 h-40 p-2 border border-gray-400"
                  placeholder={formatMessage(messages.refererNotePlaceholder)}
                  value={state.referer_note}
                  onChange={({ target: { value } }) => setState({ ...state, referer_note: value })}
                />
              }
            </div>
          </section>

          <section className="mt-6">
            <p className="py-4">
              <FormattedMessage {...messages.applicationLabel } /> *
            </p>

            <textarea
              value={state.application}
              onChange={({ target: { value } }) => {
                if (value.length < 501) {
                  setState({ ...state, application: value })
                }
              }}
              placeholder={formatMessage(messages.applicationPlaceholder)}
              className={`w-full p-4 focus:border-primary transition duration-200 border ${errors.application ? 'border-gray-400' : 'border-red-600'}`}
              onBlur={validate}
            />

            <p className="text-xs py-2 font-light text-gray-600">
              <FormattedMessage {...messages.applicationHint} values={{ left: 500 - state.application.length, max: 500, min: 20  }} />
            </p>
          </section>

          <Button
            onClick={loading ? undefined : onSubmit}
            className="float-right w-32"
          >
            { loading ? (
              <FormattedMessage {...messages.loading} />
            ) : (
              <FormattedMessage {...messages.apply} />
            ) }
          </Button>
        </div>
      </div>
    </div>
  )
}

export default Form
